/**
 * LearningView — Phase 1 learning progress panel.
 *
 * Displays per-layer coverage for all 9 foundational layers and corpus
 * ingest totals. Polls GET /api/v1/admin/learning/phase1/progress every 5 s.
 *
 * Auth: X-Founder-Signature (Ed25519 over SHA-256 of the route path).
 * The route bypasses the JWT middleware — the founder signature is the
 * sole auth gate (same pattern as omega-amend, brain controls).
 *
 * The signature is obtained once on mount via the warm-key sign-hash
 * endpoint (POST /admin/founder/sign-hash) and cached in a ref for the
 * duration of the polling session.
 *
 * Sprint 23 Step 3c VISIBILITY — Founder-only panel.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiBaseUrl } from '@/api/http';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { cn } from '@/utils/cn';

// ── Types ──────────────────────────────────────────────────────────────────────

interface LayerProgress {
  layer: string;
  coverage_pct: number;
  atoms_out: number;
  completed: boolean;
  last_updated_unix: number;
}

interface IngestStatus {
  sources_loaded: number;
  atoms_in: number;
}

interface ProgressSnapshot {
  layers: LayerProgress[];
  ingest_status: IngestStatus;
  as_of_unix: number;
  // Q7: source-of-truth counts (vs the sources_loaded/atoms_in 1:1 proxy).
  atom_content_count: number;
  fully_tagged_count: number;
}

// ── Auth helpers ───────────────────────────────────────────────────────────────

/** Fixed message the backend expects the founder to sign. */
const SIGNED_MESSAGE = 'GET /api/v1/admin/learning/phase1/progress';

/**
 * Compute SHA-256 of SIGNED_MESSAGE using the Web Crypto API.
 * Returns the digest as 64 lowercase hex chars (64 chars = 32 bytes).
 */
async function computeProgressPathHash(): Promise<string> {
  const msg = new TextEncoder().encode(SIGNED_MESSAGE);
  const buf = await crypto.subtle.digest('SHA-256', msg);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Data fetch ─────────────────────────────────────────────────────────────────

async function fetchLearningProgress(sig: string): Promise<ProgressSnapshot> {
  const resp = await fetch(`${apiBaseUrl()}/admin/learning/phase1/progress`, {
    headers: { 'X-Founder-Signature': sig },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<ProgressSnapshot>;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Humanise a kebab-case FoundationalLayer variant name for display.
 * e.g. "risk-and-uncertainty" → "Risk And Uncertainty"
 */
function humaniseLayer(layer: string): string {
  return layer
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ── Components ─────────────────────────────────────────────────────────────────

function CoverageBar({ pct }: { pct: number }) {
  const filled = Math.min(Math.max(pct, 0), 100);
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-border">
      <div
        className={cn(
          'h-full rounded-full transition-all',
          filled >= 100 ? 'bg-success' : filled > 0 ? 'bg-spark' : 'bg-border',
        )}
        style={{ width: `${filled}%` }}
        aria-label={`${filled.toFixed(1)}% coverage`}
      />
    </div>
  );
}

function LayerRow({ layer }: { layer: LayerProgress }) {
  return (
    <tr className="border-t border-border">
      <td className="px-3 py-2 font-medium text-ink">{humaniseLayer(layer.layer)}</td>
      <td className="px-3 py-2" style={{ minWidth: '120px' }}>
        <CoverageBar pct={layer.coverage_pct} />
        <span className="mt-0.5 block text-[10px] text-ink-muted">
          {layer.coverage_pct.toFixed(1)}%
        </span>
      </td>
      <td className="px-3 py-2 text-center">
        <span className="inline-flex items-center rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[10px] font-mono text-ink-muted">
          {layer.atoms_out}
        </span>
      </td>
      <td className="px-3 py-2 text-center text-base leading-none">
        {layer.completed ? '✅' : '⏳'}
      </td>
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * Render the Phase 1 learning progress panel.
 *
 * Obtains the X-Founder-Signature once on mount (via the warm-key sign-hash
 * endpoint), then polls the progress endpoint every 5 seconds with that
 * cached signature. Shows a loading state while the signature is being
 * acquired and an error message if auth or the endpoint fails.
 */
export function LearningView() {
  // Cached founder signature — acquired once on mount, reused for all polls.
  const sigRef = useRef<string | null>(null);
  const [sigError, setSigError] = useState<string | null>(null);
  const [sigReady, setSigReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const hashHex = await computeProgressPathHash();
        const sig = await signHashWithFounderKey(hashHex);
        if (!cancelled) {
          sigRef.current = sig;
          setSigReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setSigError(e instanceof Error ? e.message : 'Founder signature failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { data, isLoading, error } = useQuery<ProgressSnapshot, Error>({
    queryKey: ['learning-progress'],
    queryFn: () => fetchLearningProgress(sigRef.current!),
    refetchInterval: 5_000,
    enabled: sigReady,
    // Do not throw — show inline error state instead.
    retry: 1,
  });

  return (
    <section
      aria-labelledby="learning-view-heading"
      className="rounded-xl border border-border bg-surface p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="learning-view-heading" className="text-lg font-semibold text-ink">
            Phase 1 Learning Progress
          </h2>
          <p className="mt-1 text-sm text-ink-muted">
            Foundational layer coverage from the perpetual learning scheduler.
            Auto-refreshes every 5 s.
          </p>
        </div>
        {data && (
          <span className="shrink-0 text-[10px] text-ink-subtle">
            as of {new Date(data.as_of_unix * 1000).toLocaleTimeString()}
          </span>
        )}
      </div>

      {sigError && (
        <p className="mt-5 text-sm text-danger">Founder auth failed: {sigError}</p>
      )}

      {!sigReady && !sigError && (
        <p className="mt-5 text-sm text-ink-muted">Acquiring founder signature&hellip;</p>
      )}

      {sigReady && isLoading && (
        <p className="mt-5 text-sm text-ink-muted">Loading progress&hellip;</p>
      )}

      {sigReady && error && (
        <p className="mt-5 text-sm text-danger">Could not load progress: {error.message}</p>
      )}

      {data && (
        <>
          {/* Layer table */}
          <div className="mt-5 overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                  <th className="px-3 py-2 text-start font-medium">Layer</th>
                  <th className="px-3 py-2 text-start font-medium">Coverage</th>
                  <th className="px-3 py-2 text-center font-medium">Atoms</th>
                  <th className="px-3 py-2 text-center font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.layers.map((layer) => (
                  <LayerRow key={layer.layer} layer={layer} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Ingest status footer — Q7: source-of-truth counts first; the
              cumulative sources-fetched proxy is labelled + de-emphasised (it is
              NOT the stored-atom count). The misleading atoms_in (= sources, 1:1)
              proxy is no longer shown. */}
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-ink-muted">
            <span>
              Stored content atoms:{' '}
              <strong className="text-ink">{data.atom_content_count.toLocaleString()}</strong>
            </span>
            <span>
              Fully classified (3-tag):{' '}
              <strong className="text-ink">{data.fully_tagged_count.toLocaleString()}</strong>
            </span>
            <span className="opacity-60">
              Sources fetched (cumulative):{' '}
              <strong className="text-ink">{data.ingest_status.sources_loaded.toLocaleString()}</strong>
            </span>
          </div>
        </>
      )}
    </section>
  );
}

export default LearningView;
